<template>
  <v-data-table
    class="elevation-0 rounded-0 version-list custom-table"
    item-key="id"
    :headers="headers"
    :items="versions"
    :loading="loading"
    :options="options"
    :server-items-length="total"
    :footer-props="{
      showFirstLastPage: true,
      firstIcon: 'mdi-chevron-double-left',
      lastIcon: 'mdi-chevron-double-right',
      prevIcon: 'mdi-chevron-left',
      nextIcon: 'mdi-chevron-right',
      itemsPerPageText: 'Row/Page',
      itemsPerPageOptions: [15, 30, 50, 100],
    }"
    :item-class="rowClass"
    @update:options="$emit('update:options', $event)"
    @click:row="onRowClick">
    <template v-slot:[`item.name`]="{ item }">
      <span class="font-weight-medium">{{ item.name || '—' }}</span>
    </template>

    <template v-slot:[`item.description`]="{ item }">
      <v-tooltip bottom max-width="400" :disabled="!isTruncated(item.description)">
        <template v-slot:activator="{ on, attrs }">
          <span class="text--secondary" v-bind="attrs" v-on="on">
            {{ snippet(item.description) }}
          </span>
        </template>
        <span>{{ item.description }}</span>
      </v-tooltip>
    </template>

    <template v-slot:[`item.releaseDate`]="{ item }">
      <span>{{ item.releaseDate ? $formatDate(item.releaseDate) : '—' }}</span>
    </template>

    <template v-slot:[`item.released`]="{ item }">
      <v-chip v-if="isReleased(item)" x-small color="success" text-color="white">Released</v-chip>
      <v-chip v-else x-small color="grey" text-color="white">Unreleased</v-chip>
    </template>

    <template v-slot:[`item.actions`]="{ item }">
      <v-btn
        v-if="item.existingReleaseId"
        text
        small
        color="primary"
        @click.stop="$emit('open-existing', item.existingReleaseId)">
        Open existing release
        <v-icon right small>mdi-arrow-right</v-icon>
      </v-btn>
      <v-btn v-else small color="primary" @click.stop="$emit('select', item)">
        <v-icon left small>mdi-check</v-icon>
        Select
      </v-btn>
    </template>

    <template v-slot:no-data>
      <div class="text-center d-flex flex-column">
        <img width="240" src="/img/notFound.svg" class="py-5 my-8" style="opacity: 0.54" />
        <p class="font-weight-medium">No Jira versions match these filters.</p>
      </div>
    </template>

    <template v-slot:loading>
      <div class="text-center d-flex flex-column">
        <img width="240" src="/img/loading.svg" class="py-5 my-8" style="opacity: 0.54" />
        <p class="font-weight-medium">Loading versions...</p>
      </div>
    </template>
  </v-data-table>
</template>

<script>
  import { $formatDate } from '@Utils';

  const DESCRIPTION_SNIPPET_LENGTH = 90;

  export default {
    name: 'VersionList',
    props: {
      versions: {
        type: Array,
        default: () => [],
      },
      loading: {
        type: Boolean,
        default: false,
      },
      total: {
        type: Number,
        default: 0,
      },
      options: {
        type: Object,
        default: () => ({}),
      },
    },
    data() {
      return {
        // The Jira versions endpoint only accepts projectKey/status/search/page/itemsPerPage —
        // there is no server-side sort, so no column advertises one.
        headers: [
          { text: 'Version', value: 'name', sortable: false },
          { text: 'Description', value: 'description', sortable: false },
          { text: 'Release date', value: 'releaseDate', width: '140px', sortable: false },
          { text: 'Status', value: 'released', width: '130px', sortable: false },
          { text: '', value: 'actions', width: '210px', align: 'end', sortable: false },
        ],
      };
    },
    methods: {
      $formatDate,

      /**
       * A version already turned into a release cannot be picked again — the server answers the
       * generate call with a 409, so the row is greyed out and offers a jump to that release.
       * @param {object} item Jira version row
       * @returns {string} row class
       */
      rowClass(item) {
        return item && item.existingReleaseId ? 'version-list__row--taken' : '';
      },

      /**
       * The API field name for "this version has shipped" is not pinned down by the plan, so both
       * spellings are probed before falling back to "unreleased".
       * @param {object} item Jira version row
       * @returns {boolean}
       */
      isReleased(item) {
        if (!item) return false;
        if (typeof item.released === 'boolean') return item.released;
        if (typeof item.isReleased === 'boolean') return item.isReleased;
        return false;
      },

      snippet(text) {
        if (!text) return '—';
        const value = String(text).trim();
        if (value.length <= DESCRIPTION_SNIPPET_LENGTH) return value;
        return `${value.slice(0, DESCRIPTION_SNIPPET_LENGTH)}…`;
      },

      isTruncated(text) {
        return !!text && String(text).trim().length > DESCRIPTION_SNIPPET_LENGTH;
      },

      onRowClick(item) {
        if (!item || item.existingReleaseId) return;
        this.$emit('select', item);
      },
    },
  };
</script>

<style scoped lang="scss">
  .version-list {
    ::v-deep tbody tr {
      cursor: pointer;
    }

    ::v-deep tbody tr.version-list__row--taken {
      cursor: default;
      opacity: 0.55;
    }
  }
</style>
